import { useState, useEffect, useCallback } from 'react';
import { authFetch } from '../utils/api';

export function useSandbox() {
  const [baseline, setBaseline] = useState(null);
  const [grid,     setGrid]     = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);

  // Load baseline from API
  const loadBaseline = useCallback(() => {
    setLoading(true);
    authFetch('/api/sandbox/baseline')
      .then(r => { if (!r.ok) throw new Error(r.statusText); return r.json(); })
      .then(data => {
        setBaseline(data);
        setGrid(JSON.parse(JSON.stringify(data)));
        setLoading(false);
      })
      .catch(e => { setError(e.message); setLoading(false); });
  }, []);

  useEffect(() => { loadBaseline(); }, [loadBaseline]);

  // Update a single forecast cell
  const updateCell = useCallback((categoryIndex, monthIndex, newValue) => {
    setGrid(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      const row  = next.rows[categoryIndex];
      row.months[monthIndex].value = parseFloat(newValue) || 0;
      row.total    = parseFloat(row.months.reduce((s,m) => s + m.value, 0).toFixed(2));
      row.variance = parseFloat((row.total - row.budgetTotal).toFixed(2));
      return next;
    });
  }, []);

  // Reset grid back to baseline
  const resetGrid = useCallback(() => {
    if (baseline) setGrid(JSON.parse(JSON.stringify(baseline)));
  }, [baseline]);

  // Check if a cell differs from baseline
  const isModified = useCallback((categoryIndex, monthIndex) => {
    if (!baseline || !grid) return false;
    const base = baseline.rows[categoryIndex]?.months[monthIndex]?.value;
    const curr = grid.rows[categoryIndex]?.months[monthIndex]?.value;
    return base !== curr;
  }, [baseline, grid]);

  // Shared row-classification logic used by both calcNetIncome and baselineNet
  function calcNetFromRows(rows) {
    let forecastNet = 0, budgetNet = 0;
    rows.forEach(row => {
      const s = (row.section || '').toLowerCase();
      const isIncome     = s === 'income';
      const isOtherInc   = s === 'otherincome'   || s === 'other income';
      const isCogs       = s === 'cogs'           || s === 'cost of goods sold';
      const isExpense    = s === 'expense'        || s === 'expenses';
      const isOtherExp   = s === 'otherexpense'   || s === 'other expense'  ||
                           s === 'otherexpenses'  || s === 'other expenses';
      const isUnbudgeted = row.isUnbudgeted === true;

      if (isIncome || isOtherInc) {
        forecastNet += row.total;
        budgetNet   += row.budgetTotal;
      } else if (isCogs || isExpense || isOtherExp) {
        forecastNet -= row.total;
        budgetNet   -= row.budgetTotal;
      } else if (isUnbudgeted) {
        forecastNet -= row.total;
      }
    });
    return {
      forecastNet: parseFloat(forecastNet.toFixed(2)),
      budgetNet:   parseFloat(budgetNet.toFixed(2)),
      variance:    parseFloat((forecastNet - budgetNet).toFixed(2)),
    };
  }

  // Calculate net income totals from current grid
  const calcNetIncome = useCallback(() => {
    if (!grid) return { forecastNet: 0, budgetNet: 0, variance: 0 };
    return calcNetFromRows(grid.rows);
  }, [grid]);

  // Baseline net — same logic, applied to the original baseline snapshot
  const baselineNet = baseline
    ? calcNetFromRows(baseline.rows).forecastNet
    : 0;

  return {
    baseline, grid, loading, error,
    updateCell, resetGrid, isModified,
    calcNetIncome, baselineNet, reload: loadBaseline,
  };
}
